/**
 * Parallax effect utilities
 * Provides smooth parallax scrolling effects for various elements
 */

export interface ParallaxOptions {
  speed?: number;
  direction?: 'vertical' | 'horizontal';
  reverse?: boolean;
  maxOffset?: number;
}

/**
 * Applies parallax effect to an element based on scroll position
 * @param element - The element to apply parallax to
 * @param options - Parallax configuration options
 */
export const applyParallaxEffect = (
  element: HTMLElement | null,
  options: ParallaxOptions = {}
): void => {
  if (!element) return;

  const {
    speed = 0.1,
    direction = 'vertical',
    reverse = false,
    maxOffset = 100,
  } = options;

  const handleScroll = () => {
    const scrollPosition = window.scrollY;
    const elementPosition = element.getBoundingClientRect().top + scrollPosition;
    const viewportHeight = window.innerHeight;
    
    // Calculate when element is in viewport
    const isInViewport = 
      scrollPosition + viewportHeight > elementPosition &&
      scrollPosition < elementPosition + element.offsetHeight;
    
    if (isInViewport) {
      // Calculate relative position in viewport (0 to 1)
      const relativePosition = (scrollPosition + viewportHeight - elementPosition) / 
        (viewportHeight + element.offsetHeight);
      
      // Calculate offset based on speed and direction
      let offset = relativePosition * speed * maxOffset;
      if (reverse) offset *= -1;
      
      // Apply transform based on direction
      if (direction === 'vertical') {
        element.style.transform = `translate3d(0, ${offset}px, 0)`;
      } else {
        element.style.transform = `translate3d(${offset}px, 0, 0)`;
      }
    }
  };

  // Add scroll event listener
  window.addEventListener('scroll', handleScroll);
  
  // Initial calculation
  handleScroll();
  
  // Store cleanup function on element
  (element as any).__parallaxCleanup = () => {
    window.removeEventListener('scroll', handleScroll);
  };
};

/**
 * Removes parallax effect from an element
 * @param element - The element to remove parallax from
 */
export const removeParallaxEffect = (element: HTMLElement | null): void => {
  if (!element) return;
  
  if ((element as any).__parallaxCleanup) {
    (element as any).__parallaxCleanup();
    delete (element as any).__parallaxCleanup;
  }
  
  element.style.transform = '';
};

/**
 * Creates a parallax background effect that moves at different speeds
 * @param container - The container element
 */
export const createParallaxLayers = (container: HTMLElement | null): void => {
  if (!container) return;
  
  const layers = container.querySelectorAll('.parallax-layer');
  
  layers.forEach((layer, index) => {
    const htmlLayer = layer as HTMLElement;
    const depth = htmlLayer.getAttribute('data-depth') || String((index + 1) * 0.1);
    
    applyParallaxEffect(htmlLayer, {
      speed: parseFloat(depth),
      reverse: htmlLayer.getAttribute('data-reverse') === 'true',
      direction: (htmlLayer.getAttribute('data-direction') as 'vertical' | 'horizontal') || 'vertical',
      maxOffset: parseFloat(htmlLayer.getAttribute('data-max-offset') || '100'),
    });
  });
};

/**
 * Initializes all parallax elements on the page
 */
export const initParallaxEffects = (): void => {
  // Initialize parallax containers
  const containers = document.querySelectorAll('.parallax-container');
  containers.forEach(container => {
    createParallaxLayers(container as HTMLElement);
  });
  
  // Initialize individual parallax elements
  const elements = document.querySelectorAll('.parallax-element');
  elements.forEach(element => {
    const htmlElement = element as HTMLElement;
    const speed = parseFloat(htmlElement.getAttribute('data-speed') || '0.1');
    const direction = (htmlElement.getAttribute('data-direction') as 'vertical' | 'horizontal') || 'vertical';
    const reverse = htmlElement.getAttribute('data-reverse') === 'true';
    
    applyParallaxEffect(htmlElement, { speed, direction, reverse });
  });
};

/**
 * Cleans up all parallax effects
 */
export const cleanupParallaxEffects = (): void => {
  const elements = document.querySelectorAll('.parallax-element, .parallax-layer');
  elements.forEach(element => {
    removeParallaxEffect(element as HTMLElement);
  });
};
